import { ICompactProduct } from "api/interfaces/get-page-details/common/CompactProduct.type";
import classNames from "classnames";
import StarRatings from "component/design-system/StarRatings";
import Popover from "component/design-system/popover/Popover";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import PartNumber from "component/product/PartNumber";
import SafeLink from "modules/components/SafeLink";
import SocialProof from "modules/product/social-proof/SocialProof";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { ReactNode } from "react";
import HideIf from "util/HideIf";
import { isOEHP } from "util/helper/helper";
import { isApparelProduct } from "util/product/apparel";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import { getProductHeading } from "../../../util/product/get-product-heading";
import styles from "./productCards.module.scss";
interface IProps {
  product: ICompactProduct;
  className?: string;
  hideSocialProofing?: boolean;
  hrefProps?: string;
  asPathProps?: string;
  isListView?: boolean;
  children?: ReactNode;
}

function ProductDetails(props: IProps) {
  const {
    product, className = "", hideSocialProofing, hrefProps, asPathProps, isListView = false,
    children,
  } = props;
  const router = useRouter();
  const { asPath: routeAsPath, query } = router;
  const { canonicalUrl } = product;
  let updatedHref = hrefProps;
  let updatedAsPath = asPathProps;
  if (!hrefProps) {
    const { href, asPath } = getHrefAndPath({ asPath: canonicalUrl });
    updatedHref = href;
    updatedAsPath = asPath;
  }
  const isOEPage = isOEHP(routeAsPath);
  const { oePartNumber } = query;
  const { t } = useTranslation("category");

  const averageRating = product?.averageRating || 0;
  const totalReviewsCount = product?.totalReviewsCount || 0;

  const isApparel = isApparelProduct(product);

  return (
    <div className={`${styles.product_card_container} ${className}`}>
      <div>
        {children}
        <SafeLink href={updatedHref} asPath={updatedAsPath}>
          <div className={styles.product_title}>
            <PAText component="h3" fontSize="sm" className={`${styles.product_title_text} m-0`}>
              {getProductHeading(product)}
            </PAText>
          </div>
        </SafeLink>

        <HideIf show={!isApparel}>
          <SafeLink href={updatedHref} asPath={updatedAsPath}>
            <PartNumber product={product} className={styles.part_number} />
          </SafeLink>
        </HideIf>
        <div className={classNames({ [styles.reviews_bought_container]: isListView })}>
          <HideIf show={totalReviewsCount}>
            <div className={styles.star_container}>
              <StarRatings
                rating={averageRating}
                filled
                className={styles.stars}
              />
              <PAText fontSize={FONT_SIZE[0]} className={styles.rating_text} color="black">
                {`${averageRating.toFixed(1)} (${totalReviewsCount})`}
              </PAText>
            </div>
          </HideIf>
          <HideIf show={product.boughtInLastMonth && !hideSocialProofing && (isListView && totalReviewsCount)}>
            <span className={styles.separator}>•</span>
          </HideIf>

          <HideIf show={!hideSocialProofing}>
            <SocialProof product={product} categoryPage />
          </HideIf>
        </div>

        <HideIf show={isOEPage}>
          <div className={styles.oem_text}>
            <div>
              <i className="fa fa-check" />
            </div>
            <PAText color="success-green" component="div">
              {t("Perfectly replaces OEM")}
              {" "}
              #
              {oePartNumber}
              <div className={styles.popover_container}>
                <Popover
                  component={(
                    <PAText color="black">
                      <strong>
                        {t("Shop with confidence")}
                        :
                      </strong>
                      &nbsp;
                      {t("This aftermarket part is made with the exact same specifications as the OEM part it replaces")}
                      .
                    </PAText>
                  )}
                  popoverContentClassName={styles.popover_content}
                  placement="left"
                >
                  <i
                    className={`${styles.info_icon} fa fa-info-circle more-info`}
                  />

                </Popover>
              </div>
            </PAText>

          </div>

        </HideIf>
      </div>
    </div>

  );
}

export default ProductDetails;
